// src/scenes/global/Topbar.jsx
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  MenuItem,
  Menu,
  ListItemIcon,
} from "@mui/material";
import { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useTheme } from "@mui/material/styles";

import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined"; // Abonnemente

import { AuthContext } from "../../AuthContext";
import { apiGet } from "../../api";
import { isDemoMode } from "../../demoMode";
import AppBreadcrumbs from "../../design-system/components/AppBreadcrumbs";
import AppSelect from "../../design-system/components/AppSelect";
import LocationsDialog from "../settings/LocationsDialog";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { useTranslation } from "react-i18next";
import "../../i18n";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";

const getTranslation = (t, key, fallback) => {
  const value = t(key);
  return value && value !== key ? value : fallback;
};

const buildTopbarBreadcrumbs = (pathname, t) => {
  const path = pathname.toLowerCase();
  const label = (key, fallback) => getTranslation(t, key, fallback);

  if (path.startsWith("/team/") && path.endsWith("/edit")) {
    return [
      { label: label("sidebar.Manage Team", "Team"), to: "/team" },
      { label: label("team.edit_title", "Mitarbeitende bearbeiten") },
    ];
  }

  if (path.startsWith("/team/") && path.endsWith("/fact-sheet")) {
    return [
      { label: label("sidebar.Manage Team", "Team"), to: "/team" },
      { label: label("team.employee_file", "Mitarbeiterakte") },
    ];
  }

  const routes = [
    { path: "/admin/dashboard", items: [{ label: label("sidebar.Dashboard", "Dashboard") }] },
    { path: "/punch_clock", items: [{ label: label("sidebar.punch_clock", "Stempeluhr") }] },
    { path: "/availability", items: [{ label: label("sidebar.Verfügbarkeit", "Verfügbarkeit") }] },
    {
      path: "/create/availability/template",
      items: [
        { label: label("sidebar.Verfügbarkeit", "Verfügbarkeit"), to: "/availability" },
        { label: label("create_availability_template.title", "Vorlage") },
      ],
    },
    { path: "/demand", items: [{ label: label("sidebar.Planung", "Bedarfsplanung") }] },
    { path: "/projects", items: [{ label: label("sidebar.Projects", "Projekte") }] },
    { path: "/shiftplan", items: [{ label: label("sidebar.scheduler", "Schichtplan") }] },
    { path: "/team", items: [{ label: label("sidebar.Manage Team", "Team") }] },
    { path: "/approval/flow", items: [{ label: label("sidebar.approval", "Freigaben") }] },
    { path: "/report/expense", items: [{ label: label("sidebar.report_expense", "Spesenbericht") }] },
    { path: "/report/punch-clock", items: [] },
    { path: "/report/shiftplan", items: [] },
    { path: "/report/invoices", items: [{ label: label("sidebar.report", "Berichte"), to: "/report" }, { label: "Invoices" }] },
    { path: "/report/billing", items: [{ label: label("sidebar.report", "Berichte"), to: "/report" }, { label: "Billing" }] },
    { path: "/report/labor-costs", items: [{ label: label("sidebar.report", "Berichte"), to: "/report" }, { label: label("sidebar.labor_costs", "Lohnkosten") }] },
    { path: "/report", items: [{ label: label("sidebar.report", "Berichte") }] },
    { path: "/settings", items: [{ label: label("settings.title", "Einstellungen") }] },
    { path: "/features", items: [{ label: label("feature.title", "Abonnemente") }] },
    { path: "/feature/setup", items: [{ label: "Feature Setup" }] },
    { path: "/invite", items: [{ label: label("invite.title", "Einladen") }] },
    { path: "/new_user", items: [{ label: label("sidebar.Manage Team", "Team"), to: "/team" }, { label: "Neue Person" }] },
    { path: "/line", items: [{ label: "Line" }] },
    { path: "/faq", items: [{ label: "FAQ" }] },
    { path: "/planning", items: [{ label: label("sidebar.Planung", "Planung") }] },
    {
      path: "/create/planning/template",
      items: [
        { label: label("sidebar.Planung", "Planung"), to: "/Planning" },
        { label: "Vorlage" },
      ],
    },
  ];

  const match = routes.find((route) => path === route.path);
  if (match) return match.items;

  return [{ label: "TimeTab" }];
};

const Topbar = () => {
  const theme = useTheme();
  const demoMode = isDemoMode();
  const {
    logout,
    locations,
    selectedLocationId,
    setSelectedLocationId,
    syncLocations,
  } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbItems = useMemo(
    () => buildTopbarBreadcrumbs(location.pathname, t),
    [location.pathname, t]
  );
  const dsBreadcrumbItems = useMemo(
    () => breadcrumbItems.map(({ label, to, onClick }) => ({
      label,
      onClick: onClick || (to ? () => navigate(to) : undefined),
    })),
    [breadcrumbItems, navigate]
  );
  // Der Standard-Standort wird nur im Menue gekennzeichnet (label); der
  // geschlossene Trigger zeigt weiterhin den reinen Namen (displayLabel).
  const locationOptions = useMemo(
    () => locations.map((item) => ({
      value: String(item.id),
      label: item.is_primary
        ? `${item.name} (${t("team.primary_location_marker") || "Standard"})`
        : item.name,
      displayLabel: item.name,
    })),
    [locations, t]
  );

  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [locationsDialogOpen, setLocationsDialogOpen] = useState(false);

  const [user, setUser] = useState({ accessLevel: "" });
  const isSuperAdmin = user.accessLevel === "Super_Admin";

  // Bei einem einzigen Standort gibt es nichts zu waehlen — die Auswahl waere ein
  // Bedienelement ohne Wirkung und bleibt deshalb weg.
  // Der Super_Admin sieht sie immer: fuer ihn ist der aktive Standort Arbeitsstand,
  // nicht Beiwerk, und er kommt hier direkt an die Standortverwaltung.
  // Achtung: "Standorte verwalten" haengt an dieser Auswahl und ist damit der
  // einzige Weg dorthin, seit die Karte in /settings entfernt wurde.
  const showLocationPicker = !demoMode
    && locations.length > 0
    && (locations.length > 1 || isSuperAdmin);

  // --- User laden (wie gehabt) ---
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const data = await apiGet("/api/web/current_user", logout);
        if (!data) return;
        setUser({ accessLevel: data.access_level });
        if (!demoMode) {
          syncLocations(data.locations || [], data.selected_location_id);
        }
      } catch (e) {
        console.error("❌ Error fetching user data:", e);
      }
    };
    fetchUserData();
  }, [demoMode, logout, syncLocations]);

  // Das Mitteilungen-Menü ist bewusst entfernt (Glocke, Popover und der
  // 60-Sekunden-Poll auf /api/app/notifications). Kommt es zurueck, gehoert es
  // an diese Stelle.

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        zIndex: 10,
      }}
    >
      <Toolbar
        variant="dense"
        disableGutters
        sx={{
          minHeight: 40,
          px: 2,
          // 1fr auto 1fr wie .ds-topbar im Design-System: die mittlere Spalte sitzt
          // dadurch in der echten Mitte der Topbar und verschiebt sich nicht, wenn
          // links die Breadcrumbs oder rechts die Icons unterschiedlich breit sind.
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            overflow: "hidden",
          }}
        >
          <AppBreadcrumbs items={dsBreadcrumbItems} />
        </Box>

        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // Lokale Abweichungen vom DS (bewusst hier statt im DS definiert):
            // 1. Feste Breite, damit die mittlere Grid-Spalte beim Wechsel zwischen
            //    kurzen und langen Standortnamen ihre Breite behaelt und die
            //    Zentrierung nicht springt. Breit genug fuer lange Standortnamen.
            "& .ds-select-wrap": { width: 280 },
            // 2. Eine Stufe kleiner als die kleinste AppSelect-Groesse: die
            //    Komponente kennt nur sm (32px) und md (36px), daher hier auf das
            //    vorhandene Token --ds-control-height-xs (28px) heruntergesetzt.
            "& .ds-select": {
              height: "var(--ds-control-height-xs)",
              minHeight: "var(--ds-control-height-xs)",
              fontSize: "var(--ds-typo-body-2-size)",
            },
            "& .ds-select__value": {
              fontSize: "var(--ds-typo-body-2-size)",
            },
            // 3. Nur der Pin traegt die Brand-Farbe. variant="brand" waere flaechig
            //    hinterlegt, was in der Topbar unruhig wirkt.
            "& .ds-select__leading-icon": {
              color: "var(--ds-color-brand-default)",
            },
          }}
        >
          {showLocationPicker ? (
            <AppSelect
              size="sm"
              leadingIcon={LocationOnOutlinedIcon}
              value={selectedLocationId || String(locations[0]?.id || "")}
              onChange={(event) => setSelectedLocationId(event.target.value)}
              options={locationOptions}
              placeholder="Standort"
              footerAction={
                ["Super_Admin", "Admin", "company_admin"].includes(user.accessLevel)
                  ? {
                      label: "Standorte verwalten",
                      icon: <TuneOutlinedIcon fontSize="small" />,
                      onClick: () => setLocationsDialogOpen(true),
                    }
                  : null
              }
            />
          ) : null}
        </Box>

        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            // Kleinster DS-Aktionsabstand zwischen den Icon-Aktionen — die
            // IconButtons bringen daneben schon ihr eigenes Innenpolster mit.
            gap: "var(--ds-action-gap-xs)",
          }}
        >
          {/* Settings (schwarz) */}
          {!demoMode ? (
            <>
              <IconButton
                onClick={(e) => setSettingsAnchor(e.currentTarget)}
                sx={{ color: theme.palette.grey[900] }}
              >
                <SettingsOutlinedIcon />
              </IconButton>
              <Menu
                anchorEl={settingsAnchor}
                open={Boolean(settingsAnchor)}
                onClose={() => setSettingsAnchor(null)}
              >
              <MenuItem
                onClick={() => {
                  setSettingsAnchor(null);
                  navigate("/settings");
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.text.primary, minWidth: 32 }}>
                  <SettingsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {t("settings.title")}
              </MenuItem>

                {/* Abonnemente — nur Super_Admin, siehe Route /features in App.js */}
                {user.accessLevel === "Super_Admin" && (
                  <MenuItem
                    onClick={() => {
                      setSettingsAnchor(null);
                      navigate("/features");
                    }}
                  >
                    <ListItemIcon sx={{ color: theme.palette.text.primary, minWidth: 32 }}>
                      <CreditCardOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    {t("feature.title")}
                  </MenuItem>
                )}
              </Menu>
            </>
          ) : null}

          {/* Logout (schwarz) */}
          <IconButton onClick={logout} sx={{ color: theme.palette.grey[900] }}>
            <LogoutIcon />
          </IconButton>
        </Box>
      </Toolbar>

      <LocationsDialog
        open={locationsDialogOpen}
        onClose={() => setLocationsDialogOpen(false)}
      />
    </AppBar>
  );
};

export default Topbar;
